import React, { useMemo } from 'react';
import { LineChart } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, DashboardComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';

type AdminPanelLineChartProps = {
  component: SupportedDashboardComponent & { kind: 'Line Chart' };
  queryResult: DashboardComponentResult;
  height?: number;
  tableCaption?: string;
  showDataTable?: boolean;
};

export const AdminPanelLineChart: React.FC<AdminPanelLineChartProps> = ({
  component,
  queryResult,
  height = 300,
  tableCaption,
  showDataTable,
}) => {
  const { processedData, categoryKey, valueKey, dateFormat } = useMemo(() => {
    // Use backend-provided group key (with fallback for older responses)
    const primaryGroupField = queryResult.groupKeys?.[0] || '';
    let dateFormat: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' | undefined;

    const bucketGroup = component.groups[0];
    dateFormat = bucketGroup.bucket;

    const summaryKey = queryResult.summaryKey || '';

    // Check if we have multiple lines (secondary field in lines config)
    const linesConfig = (component as any).lines;
    const hasSecondaryGrouping = linesConfig && linesConfig.kind === 'Grouped' && linesConfig.values?.[0]?.field;

    // Filter out null/invalid dates and convert string values to numbers for proper Y-axis scaling
    let processedData = queryResult.data
      .filter((item: Record<string, any>) => {
        const dateValue = item[primaryGroupField];
        return dateValue != null && dateValue !== '';
      })
      .map((item: Record<string, any>) => ({
        ...item,
        [summaryKey]: Number(item[summaryKey])
      }));
    let valueKeys = [summaryKey]; // Default to original summaryKey for single line

    if (hasSecondaryGrouping) {
      const secondaryField = linesConfig.values[0].field;

      // Group data by primary field and pivot secondary field
      const groupedData: Record<string, Record<string, any>> = {};
      const secondaryValueSet = new Set<string>();

      processedData.forEach((row: any) => {
        const primaryValue = String(row[primaryGroupField] || 'Unknown');
        const secondaryValue = String(row[secondaryField] || 'null');
        const aggregateValue = Number(row[summaryKey]) || 0;

        if (!groupedData[primaryValue]) {
          groupedData[primaryValue] = { [primaryGroupField]: primaryValue };
        }

        secondaryValueSet.add(secondaryValue);

        if (!groupedData[primaryValue][secondaryValue]) {
          groupedData[primaryValue][secondaryValue] = 0;
        }
        groupedData[primaryValue][secondaryValue] += aggregateValue;
      });

      processedData = Object.values(groupedData);
      valueKeys = Array.from(secondaryValueSet); // Use secondary field values as value keys
    }

    return {
      processedData,
      categoryKey: primaryGroupField,
      valueKey: valueKeys.length === 1 ? valueKeys[0] : valueKeys,
      dateFormat,
    };
  }, [component, queryResult]);

  return (
    <LineChart
      data={processedData}
      categoryKey={categoryKey}
      valueKey={valueKey}
      xAxisLabel={component.xAxisLabel}
      yAxisLabel={component.yAxisLabel}
      height={height}
      tableCaption={tableCaption}
      dateFormat={dateFormat}
      showDataTable={showDataTable}
      showPercentages={component.showPercentages === true}
    />
  );
};
